<template>
  <v-card
    v-if="viewData"
    flat
  >
    <v-card-title class="py-0 px-0">
      <v-toolbar
        :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
        class="filter-toolbar"
        flat
      >
        <v-btn
          v-if="!appNotifySmsActive"
          color="primary"
          @click="onRequestDialogShow"
        >
          {{ $t('Settings.Notification.SupportRequest.Contact') }}
        </v-btn>
        <v-btn
          v-else-if="userCanAdd"
          :color="$t('Marketing.Sms.ListView.Button.Add.Color')"
          :icon="$vuetify.breakpoint.xsOnly"
          class="elevation-0 ma-1"
          text
          @click="showTemplatesDialogInSelectMode"
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Marketing.Sms.ListView.Button.Add.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Marketing.Sms.ListView.Button.Add.Title') }}</span>
        </v-btn>

        <v-btn
          v-if="userCanAccess"
          :color="$t('Marketing.Sms.ListView.Button.ViewTemplate.Color')"
          :icon="$vuetify.breakpoint.xsOnly"
          class="elevation-0 ma-1"
          text
          @click="showTemplatesDialog('edit')"
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Marketing.Sms.ListView.Button.ViewTemplate.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Marketing.Sms.ListView.Button.ViewTemplate.Title') }}</span>
        </v-btn>

        <v-spacer />

        <v-sheet
          :color="$vuetify.theme.dark ? '#1E1E1E' : '#0000000F'"
          class="pl-0 pr-3 align-content-center font-weight-bold text-body-1"
          height="48"
          rounded
        >
          <v-avatar
            :color="appNotifySmsColor"
            class="mr-2 rounded-l"
            size="48"
            tile
          >
            <v-icon
              dark
              size="28"
              style="opacity: 0.65"
            >
              mdi-cellphone-message
            </v-icon>
          </v-avatar>

          <span class="grey--text text--darken-1">{{ $t('Settings.Notification.Data.StoreSms.Credits') }}:</span> <span
            :class="`${appNotifySmsColor}--text`"
          >{{ appNotifySmsCredits }}</span>
        </v-sheet>

        <filter-search v-model="dataTable.search.terms" />
      </v-toolbar>
    </v-card-title>

    <v-container
      class="pa-0"
      fluid
    >
      <v-row>
        <v-col cols="12">
          <v-data-table
            :footer-props="dataTable.footerProps"
            :headers="dataTableHeaders"
            :height="dataTable.resize.bodyHeight"
            :items="dataTable.data"
            :loading="dataTable.loading"
            :loading-text="dataTable.text.loadingText"
            :no-data-text="dataTable.text.noDataText"
            :no-results-text="dataTable.text.noResultsText"
            :options.sync="dataTable.options"
            :search="dataTable.search.terms"
            :server-items-length="dataTable.total"
            fixed-header
            item-key="Id"
          >
            <template #[`item.id`]="{item}">
              {{ item.Id }}
            </template>

            <template #[`item.name`]="{item}">
              {{ item.Name }}
            </template>

            <template #[`item.groups`]="{item}">
              <template v-for="(groupItem, groupItemIndex) in getGroups(item)">
                <v-sheet
                  :key="`group-item-${groupItemIndex}`"
                  :style="`border-color: ${getCssColor(groupItem.Color)}`"
                  class="my-2"
                  outlined
                  rounded
                >
                  <div class="ma-1">
                    <v-subheader
                      class="pa-0 ml-1"
                      style="height: auto; font-size: 11px;"
                    >
                      {{ groupItem.Title }}
                    </v-subheader>
                  </div>

                  <template v-for="(group, index) in groupItem.Items">
                    <v-chip
                      :key="`group-item-${groupItemIndex}-campaign-${group.Id}-group-${index}`"
                      :color="groupItem.Color"
                      class="ma-1 px-2 text-uppercase"
                      dark
                      label
                      x-small
                    >
                      {{ group.Name }}
                    </v-chip>
                  </template>
                </v-sheet>
              </template>
            </template>

            <template #[`item.tags`]="{item}">
              <template v-for="(tag, index) in getTags(item)">
                <v-chip
                  :key="`campaign-${tag.Id}-tag-${index}`"
                  class="ma-1 px-2 text-uppercase"
                  color="indigo"
                  dark
                  label
                  x-small
                >
                  {{ tag.Name }}
                </v-chip>
              </template>
            </template>

            <template #[`item.status`]="{item}">
              <v-chip
                :color="getStatus(item).Color"
                dark
                label
                small
              >
                {{ getStatus(item).Name }}
              </v-chip>
            </template>

            <template #[`item.sent`]="{item}">
              <v-chip
                label
                small
              >
                {{ formatDecimal($i18n.locale, item.Sent, 0) }}
              </v-chip>
            </template>

            <template #[`item.delivered`]="{item}">
              <v-chip
                label
                small
              >
                {{ formatDecimal($i18n.locale, item.Delivered, 0) }}
              </v-chip>
            </template>

            <template #[`item.opened`]="{item}">
              <v-chip
                label
                small
              >
                {{ formatDecimal($i18n.locale, item.Opened, 0) }}
              </v-chip>
            </template>

            <template #[`item.clicked`]="{item}">
              <v-chip
                label
                small
              >
                {{ formatDecimal($i18n.locale, item.Clicked, 0) }}
              </v-chip>
            </template>

            <template #[`item.send_at`]="{item}">
              {{ item.SendAt }}
            </template>

            <template #[`item.scheduled_at`]="{item}">
              {{ item.ScheduledAt }}
            </template>

            <template #[`item.created_at`]="{item}">
              {{ item.CreatedAt }}
            </template>

            <template #[`item.actions`]="{item}">
              <template v-if="item.Draft && (userCanAdd || userCanManage)">
                <v-tooltip
                  :color="$t('Marketing.Sms.ListView.Button.Edit.Color')"
                  :open-delay="1000"
                  left
                >
                  <template #activator="{on}">
                    <v-btn
                      :class="item.loading || item.loadingEdit ? 'grey' : $t('Marketing.Sms.ListView.Button.Edit.Color')"
                      :disabled="item.loading || item.loadingEdit"
                      :loading="item.loadingEdit"
                      :small="$vuetify.breakpoint.xsOnly"
                      class="elevation-2 ma-1"
                      dark
                      icon
                      @click="editCampaign(item)"
                      v-on="on"
                    >
                      <v-icon>{{ $t('Marketing.Sms.ListView.Button.Edit.Icon') }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Marketing.Sms.ListView.Button.Edit.Title') }}</span>
                </v-tooltip>
              </template>

              <template v-if="userCanAdd || userCanManage">
                <v-tooltip
                  :color="$t('Marketing.Sms.ListView.Button.Duplicate.Color')"
                  :open-delay="1000"
                  left
                >
                  <template #activator="{on}">
                    <v-btn
                      :class="item.loading || item.loadingDuplicate ? 'grey' : $t('Marketing.Sms.ListView.Button.Duplicate.Color')"
                      :disabled="item.loading || item.loadingDuplicate"
                      :loading="item.loadingDuplicate"
                      :small="$vuetify.breakpoint.xsOnly"
                      class="elevation-2 ma-1"
                      dark
                      icon
                      @click="duplicateCampaign(item)"
                      v-on="on"
                    >
                      <v-icon>{{ $t('Marketing.Sms.ListView.Button.Duplicate.Icon') }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Marketing.Sms.ListView.Button.Duplicate.Title') }}</span>
                </v-tooltip>
              </template>

              <template v-if="userCanDelete">
                <v-tooltip
                  :color="$t('Marketing.Sms.ListView.Button.Edit.Color')"
                  :open-delay="1000"
                  left
                >
                  <template #activator="{on}">
                    <v-btn
                      :class="!!item.DeletedAt || item.loading || item.loadingDelete ? 'grey' : $t('Marketing.Sms.ListView.Button.Delete.Color')"
                      :disabled="!!item.DeletedAt || item.loading || item.loadingDelete"
                      :loading="item.loadingDelete"
                      :small="$vuetify.breakpoint.xsOnly"
                      class="elevation-2 ma-1"
                      dark
                      icon
                      @click="deleteCampaign(item)"
                      v-on="on"
                    >
                      <v-icon>{{ $t('Marketing.Sms.ListView.Button.Delete.Icon') }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Marketing.Sms.ListView.Button.Delete.Title') }}</span>
                </v-tooltip>
              </template>
            </template>

            <template #no-data>
              <v-data-table-no-data
                :button-callback="appNotifySmsActive ? showTemplatesDialogInSelectMode : onRequestDialogShow"
                :button-color="appNotifySmsActive ? $t('Marketing.Sms.ListView.Table.NoData.Button.Color') : $t('Marketing.Sms.ListView.Table.NoData.ButtonActivate.Color')"
                :button-icon="appNotifySmsActive ? $t('Marketing.Sms.ListView.Table.NoData.Button.Icon') : $t('Marketing.Sms.ListView.Table.NoData.ButtonActivate.Icon')"
                :button-title="appNotifySmsActive ? $t('Marketing.Sms.ListView.Table.NoData.Button.Title') : $t('Marketing.Sms.ListView.Table.NoData.ButtonActivate.Title')"
                :no-data-obj="$t('Marketing.Sms.ListView.Table.NoData')"
              />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>


    <sms-templates-dialog
      :mode="templatesDialogMode"
      :visible.sync="templatesDialogVisible"
      @template:selected="createCampaignFromTemplate"
    />

    <sms-campaign-edit-dialog
      v-if="selectedCampaignTemplate"
      :groups="customerGroups"
      :template="selectedCampaignTemplate"
      :visible.sync="addCampaignDialogVisible"
    />

    <confirm-dialog
      v-if="selectedCampaignTemplate"
      :html-content="$t('Marketing.Sms.ListView.ConfirmDeleteDialog.Content', {title: selectedCampaignTemplate.Name})"
      :html-title="$t('Marketing.Sms.ListView.ConfirmDeleteDialog.Title')"
      :info-bar="true"
      :info-bar-body="$t('Marketing.Sms.ListView.ConfirmDeleteDialog.Warning')"
      :visible.sync="confirmDialogDeleteVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDeleteCampaign"
    />

    <ticket-request-dialog
      :message="ticketMessage"
      :ticket-model="ticketModel"
      :visible.sync="requestDialogVisible"
    />
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata.js'
import Auth from '@/mixins/auth.js'
import DataTable from '@/mixins/data-table/dataTable.js'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import VDataTableNoData from '@/components/common/data-table/VDataTableNoData.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import SmsCampaignEditDialog from '@/components/marketing/sms/SmsCampaignEditDialog.vue'
import SmsTemplatesDialog from '@/components/marketing/sms/SmsTemplatesDialog.vue'
import CampaignCommon from '@/mixins/marketing/campaignCommon.js'
import TicketRequestDialog from '@/components/support-center/TicketRequestDialog.vue'
import SupportTicketAddModel from '@/api/Models/support-center/SupportTicketAddModel.js'
import SupportDepartmentEnum from '@/api/Enums/SupportDepartmentEnum.js'
import SupportPriorityEnum from '@/api/Enums/SupportPriorityEnum.js'

export default {
  Name      : 'Sms',
  components: {
    TicketRequestDialog,
    SmsTemplatesDialog,
    SmsCampaignEditDialog,
    ConfirmDialog,
    VDataTableNoData,
    FilterSearch
  },
  directives: {},
  mixins    : [AppData, Auth, DataTable, CampaignCommon],
  data() {
    return {
      templatesDialogMode       : 'edit',
      addCampaignDialogVisible  : false,
      templatesDialogVisible    : false,
      confirmDialogDeleteVisible: false,
      selectedCampaignTemplate  : null,
      customerGroups            : [],
      tags                      : [],
      ticketMessage             : '',
      requestDialogVisible      : false,
      ticketModel               : new SupportTicketAddModel(),

      dataTable: {
        socketCommand: {
          list: window.SocketCommand.Marketing.Sms.All
        },
        options: {
          defaultSortBy: 'id',
          sortBy       : ['id'],
          sortDesc     : [true]
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Id'),
            value    : 'id',
            align    : 'right',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Name'),
            value    : 'name',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Groups'),
            value    : 'groups',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Tags'),
            value    : 'tags',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Status'),
            value    : 'status',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Sent'),
            value    : 'sent',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Delivered'),
            value    : 'delivered',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Opened'),
            value    : 'opened',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Clicked'),
            value    : 'clicked',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.SendAt'),
            value    : 'send_at',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.ScheduledAt'),
            value    : 'scheduled_at',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.CreatedAt'),
            value    : 'created_at',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Sms.ListView.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption px-2',
            width    : 150
          }
        ]
      }
    }
  },
  computed: {},
  watch   : {},
  created() {
    this.$bus.$on(window.SocketCommand.Marketing.Sms.Get, this.onGetCampaignResult)
    this.$bus.$on(window.SocketCommand.Marketing.Sms.GetDuplicate, this.onGetDuplicateResult)
    this.$bus.$on(window.SocketCommand.Marketing.Sms.Save, this.onSaveCampaignResult)
    this.$bus.$on(window.SocketCommand.Marketing.Sms.Delete, this.onDeleteCampaignResult)
    this.$bus.$on(window.SocketCommand.CustomerGroup.All, this.onCustomerGroupsResult)
    this.$bus.$on(window.SocketCommand.Marketing.Tag.Combo, this.onMarketingTagsResult)

    this.getCustomerGroups()
    this.getMarketingTags()
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Marketing.Sms.Get, this.onGetCampaignResult)
    this.$bus.$off(window.SocketCommand.Marketing.Sms.GetDuplicate, this.onGetDuplicateResult)
    this.$bus.$off(window.SocketCommand.Marketing.Sms.Save, this.onSaveCampaignResult)
    this.$bus.$off(window.SocketCommand.Marketing.Sms.Delete, this.onDeleteCampaignResult)
    this.$bus.$off(window.SocketCommand.CustomerGroup.All, this.onCustomerGroupsResult)
    this.$bus.$off(window.SocketCommand.Marketing.Tag.Combo, this.onMarketingTagsResult)
  },
  methods: {
    onRequestDialogShow() {
      const CurrentPlanId = this.appConfig?.LOCATION_DATA?.PackageId

      const Subject = this.$t(`Settings.Notification.SupportRequest.Sms.Subject`)
      const Message = this.$t(`Settings.Notification.SupportRequest.Sms.Message`)
      const Department = SupportDepartmentEnum.SALES
      const Priority = SupportPriorityEnum.MEDIUM
      const Service = this.$t(`Account.Plan.Package.${CurrentPlanId}.ServiceId`)

      this.ticketMessage = Message
      this.ticketModel = new SupportTicketAddModel({
        subject   : Subject,
        department: Department,
        priority  : Priority,
        service   : Service
      })

      this.requestDialogVisible = true
    },

    duplicateCampaign(campaign) {
      if (!campaign) return
      this.setCampaignLoading(campaign, 'duplicate', true)
      this.getCampaign(campaign, window.SocketCommand.Marketing.Sms.GetDuplicate)
    },

    editCampaign(campaign) {
      if (!campaign) return
      this.setCampaignLoading(campaign, 'edit', true)
      this.getCampaign(campaign)
    },

    getCampaign(campaign, returnCommand) {
      if (!campaign) return
      window.callAS(window.SocketCommand.Marketing.Sms.Get, {Id: campaign.Id}, returnCommand)
    },
    onGetCampaignResult(result, payload) {
      this.setCampaignLoading(this.getDataTableCampaign(payload?.Id), 'edit', false)

      if (result?.data && result?.status === 'success') {
        this.selectedCampaignTemplate = result.data
        this.showAddCampaignDialog()
      }
    },
    onGetDuplicateResult(result, payload) {
      if (result?.data && result?.status === 'success') {
        const payload = result.data
        payload.Id = null
        payload.Draft = true
        window.callAS(window.SocketCommand.Marketing.Sms.Save, payload)
      }
    },

    onSaveCampaignResult(result, payload) {
      if (result?.status === 'success') {
        this.getData()
      }
    },

    deleteCampaign(campaign) {
      if (!campaign) return
      this.selectedCampaignTemplate = campaign
      this.confirmDialogDeleteVisible = true
    },
    onConfirmDeleteCampaign() {
      const campaign = this.selectedCampaignTemplate
      this.setCampaignLoading(campaign, 'delete', true)
      window.callAS(window.SocketCommand.Marketing.Sms.Delete, {Id: campaign.Id})
      this.selectedCampaignTemplate = null
    },
    onDeleteCampaignResult(result, payload) {
      if (result?.status === 'success') {
        this.getData()
      } else {
        this.setCampaignLoading(this.getDataTableCampaign(payload?.Id), 'delete', false)
      }
    },

    getCustomerGroups() {
      window.callAS(window.SocketCommand.CustomerGroup.All, {page_limit: -1, config: false})
    },
    onCustomerGroupsResult(result, payload) {
      this.customerGroups = result?.items || []
    },

    getMarketingTags() {
      window.callAS(window.SocketCommand.Marketing.Tag.Combo)
    },
    onMarketingTagsResult(result, payload) {
      if (Array.isArray(result)) {
        this.tags = result || []
      }
    },

    showAddCampaignDialog() {
      this.addCampaignDialogVisible = true
    },

    showTemplatesDialogInSelectMode() {
      if (!this.appNotifySmsActive) return
      this.showTemplatesDialog('select')
    },

    showTemplatesDialog(mode = 'edit') {
      this.templatesDialogMode = mode || 'edit'
      this.templatesDialogVisible = true
    },

    createCampaignFromTemplate(template) {
      this.selectedCampaignTemplate = {
        Body      : template?.Body || '',
        SenderName: template?.SenderName || this.appNotifySmsSender || ''
      }

      this.showAddCampaignDialog()
    }
  }
}
</script>

<style scoped>
:deep(.filter-toolbar .v-toolbar__content) {
  padding: 0 6px;
}

:deep(.v-input--selection-controls.v-input--switch) {
  display: inline-block;
  position: relative !important;
  /*width    : 75px !important;*/
}

:deep(.v-input--selection-controls.v-input--switch label) {
  font-size: 12px !important;
  padding-right: 0;
  margin: 0;
}

:deep(.v-data-table > .v-data-table__wrapper > table:has(tbody tr:first-child.v-data-table__empty-wrapper)) {
  width: 100%;
  height: 100%;
  border-spacing: 0;
}
</style>
